const LLAMA_URL = "https://yields.llama.fi/pools";

const PROJECT_MAP = {
  "curve-dex":           "Curve",
  "uniswap-v3":          "Uniswap V3",
  "balancer-v2":         "Balancer",
  "pancakeswap-amm-v3":  "PancakeSwap",
  "pancakeswap-amm":     "PancakeSwap",
  "raydium-amm":         "Raydium",
  "orca-dex":            "Orca",
  "kamino-liquidity":    "Kamino",
  "uniswap-v4":          "Uniswap V4",
  "aerodrome":           "Aerodrome",
  "aerodrome-v2":        "Aerodrome",
  "velodrome":           "Velodrome",
  "velodrome-v2":        "Velodrome",
  "sushiswap":           "SushiSwap",
  "sushiswap-v3":        "SushiSwap",
  "trader-joe-dex":      "Trader Joe",
  "trader-joe-v2.1":     "Trader Joe",
  "camelot-v3":          "Camelot",
  "camelot-dex":         "Camelot",
};

const TARGET_PROJECTS = Object.keys(PROJECT_MAP);
const TARGET_CHAINS = ["Ethereum", "Arbitrum", "Optimism", "Base", "Polygon", "BSC", "Solana", "Avalanche"];

const KNOWN_STABLES = [
  "USDC", "USDT", "DAI", "FRAX", "LUSD", "sUSD", "GHO", "crvUSD",
  "USDC.e", "USDbC", "PYUSD", "USDS", "TUSD", "BUSD", "GUSD", "RAI",
  "DOLA", "MIM", "EUSD", "USD+", "USDD", "USDP", "alUSD", "eUSD",
  "FDUSD",
];

function classifyRisk(symbol, stablecoin, ilRisk) {
  if (stablecoin && ilRisk === "no") return "LOW";
  const tokens = symbol.split("-");
  const stableCount = tokens.filter(t =>
    KNOWN_STABLES.some(s => t.toUpperCase().includes(s.toUpperCase()))
  ).length;
  if (stableCount >= tokens.length) return "LOW";
  if (stableCount > 0) return "MED";
  return "HIGH";
}

function parseTokens(symbol) {
  return symbol.split("-").map(t => t.trim()).filter(Boolean);
}

export async function fetchPools() {
  try {
    const res = await fetch(LLAMA_URL);
    if (!res.ok) throw new Error('DeFi Llama responded ' + res.status);
    const json = await res.json();
    const raw = json.data;

    const filtered = raw.filter(p =>
      TARGET_PROJECTS.includes(p.project) &&
      TARGET_CHAINS.includes(p.chain) &&
      p.tvlUsd > 50000 &&
      p.apy != null &&
      p.apy > 0.01
    );

    return filtered.map((p, i) => {
      const tokens = parseTokens(p.symbol);
      const protocol = PROJECT_MAP[p.project] || p.project;
      const risk = classifyRisk(p.symbol, p.stablecoin, p.ilRisk);

      return {
        id: p.pool,
        name: tokens.join("/"),
        protocol,
        chain: p.chain,
        apr: p.apy || 0,
        tvl: p.tvlUsd || 0,
        volume24h: p.volumeUsd1d || 0,
        fees24h: p.volumeUsd1d ? p.volumeUsd1d * (parseFloat(p.poolMeta) || 0.003) / 100 : 0,
        risk,
        stablecoin: p.stablecoin || false,
        tokens,
        fee: p.poolMeta || "—",
        audited: true,
        tag: p.poolMeta || null,
        apyBase: p.apyBase || 0,
        apyReward: p.apyReward || 0,
        apyPct1D: p.apyPct1D,
        apyPct7D: p.apyPct7D,
        apyPct30D: p.apyPct30D,
        ilRisk: p.ilRisk,
        exposure: p.exposure,
        poolId: p.pool,
        underlyingTokens: p.underlyingTokens || [],
        poolMeta: p.poolMeta || null,
        poolAddress: null, // DeFi Llama pool IDs are UUIDs, not contract addresses
        anomalous: (p.stablecoin && p.apy > 100) ||
                   (p.volumeUsd1d && p.tvlUsd && (p.volumeUsd1d / p.tvlUsd) > 100) ||
                   (p.apy > 2000),
      };
    }).sort((a, b) => b.tvl - a.tvl);
  } catch (err) {
    console.warn("Failed to fetch pools:", err.message);
    return [];
  }
}

const LLAMA_CHART_URL = "https://yields.llama.fi/chart";

export async function fetchPoolHistory(poolId) {
  const res = await fetch(`${LLAMA_CHART_URL}/${poolId}`);
  if (!res.ok) throw new Error(`DeFi Llama chart responded ${res.status}`);
  const json = await res.json();
  return (json.data || []).map(d => ({
    timestamp: d.timestamp,
    tvlUsd: d.tvlUsd,
    apy: d.apy,
    apyBase: d.apyBase,
    apyReward: d.apyReward,
  }));
}

const COINGECKO_TRENDING_URL = "https://api.coingecko.com/api/v3/search/trending";

export async function fetchTrending() {
  try {
    const res = await fetch(COINGECKO_TRENDING_URL);
    if (!res.ok) throw new Error(`CoinGecko responded ${res.status}`);
    const json = await res.json();

    return (json.coins || []).map(item => {
      const coin = item.item;
      return {
        id: coin.id,
        name: coin.name,
        symbol: coin.symbol,
        thumb: coin.thumb,
        small: coin.small,
        marketCapRank: coin.market_cap_rank,
        priceBtc: coin.price_btc,
        score: coin.score,
      };
    });
  } catch (err) {
    console.warn("Failed to fetch CoinGecko trending:", err.message);
    return [];
  }
}

// ── Degen Pools: trending memecoins from DexScreener + GeckoTerminal ──

const DEXSCREENER_BOOSTS_URL = "https://api.dexscreener.com/token-boosts/top/v1";
const GECKOTERMINAL_TRENDING_URL = "https://api.geckoterminal.com/api/v2/networks/trending_pools";

const CHAIN_NAME_MAP = {
  eth: "Ethereum",
  ethereum: "Ethereum",
  arbitrum: "Arbitrum",
  optimism: "Optimism",
  base: "Base",
  polygon_pos: "Polygon",
  bsc: "BSC",
  solana: "Solana",
  avalanche: "Avalanche",
};

function normalizeChain(raw) {
  if (!raw) return "Unknown";
  return CHAIN_NAME_MAP[raw.toLowerCase()] || raw.charAt(0).toUpperCase() + raw.slice(1);
}

export async function fetchDexScreenerTrending() {
  try {
    // Step 1: fetch boosted tokens
    const res = await fetch(DEXSCREENER_BOOSTS_URL);
    if (!res.ok) throw new Error(`DexScreener responded ${res.status}`);
    const data = await res.json();
    const boosts = (data || []).slice(0, 30);

    // Step 2: group by chainId for batch pair lookups
    const chainGroups = {};
    for (const t of boosts) {
      if (!t.chainId || !t.tokenAddress) continue;
      if (!chainGroups[t.chainId]) chainGroups[t.chainId] = [];
      chainGroups[t.chainId].push(t);
    }

    // Step 3: fetch rich pair data per chain in parallel
    const pairsByAddress = {}; // lowercase tokenAddress -> best pair
    const chainFetches = Object.entries(chainGroups).map(async ([chainId, tokens]) => {
      try {
        const addresses = tokens.map(t => t.tokenAddress).join(",");
        const pairRes = await fetch(`https://api.dexscreener.com/tokens/v1/${chainId}/${addresses}`);
        if (!pairRes.ok) return;
        const pairs = await pairRes.json();
        if (!Array.isArray(pairs)) return;

        // For each pair, index by baseToken.address — keep only the highest-liquidity pair per token
        for (const pair of pairs) {
          const addr = (pair.baseToken?.address || "").toLowerCase();
          if (!addr) continue;
          const liq = pair.liquidity?.usd || 0;
          if (!pairsByAddress[addr] || liq > (pairsByAddress[addr].liquidity?.usd || 0)) {
            pairsByAddress[addr] = pair;
          }
        }
      } catch (e) {
        console.warn(`DexScreener pair fetch failed for chain ${chainId}:`, e.message);
      }
    });

    await Promise.all(chainFetches);

    // Step 4: merge boost data with pair data
    const results = boosts.map((t, i) => {
      const pair = pairsByAddress[(t.tokenAddress || "").toLowerCase()] || null;

      const volumeH24 = pair?.volume?.h24 || 0;
      const liquidityUsd = pair?.liquidity?.usd || 0;
      const dexId = pair?.dexId || "";
      const feeRate = getDexFeeRate(dexId);
      const { aprEstimated, fees24hEstimated } = estimateApr(volumeH24, liquidityUsd, feeRate);

      return {
        source: "dexscreener",
        tokenAddress: t.tokenAddress,
        chainId: t.chainId,
        chain: normalizeChain(t.chainId),
        symbol: pair?.baseToken?.symbol || (t.name || "").toUpperCase() || t.tokenAddress?.slice(0, 8),
        name: pair?.baseToken?.name || t.description || t.tokenAddress?.slice(0, 8),
        url: t.url || null,
        icon: pair?.info?.imageUrl || null,
        boostAmount: t.totalAmount || 0,
        rank: i + 1,
        // Rich data from /tokens/v1
        priceUsd: parseFloat(pair?.priceUsd) || 0,
        priceChange5m: pair?.priceChange?.m5 || 0,
        priceChange1h: pair?.priceChange?.h1 || 0,
        priceChange6h: pair?.priceChange?.h6 || 0,
        priceChange24h: pair?.priceChange?.h24 || 0,
        volume24h: volumeH24,
        volume1h: pair?.volume?.h1 || 0,
        liquidity: liquidityUsd,
        fdv: pair?.fdv || 0,
        marketCap: pair?.marketCap || 0,
        txns24hBuys: pair?.txns?.h24?.buys || 0,
        txns24hSells: pair?.txns?.h24?.sells || 0,
        pairAddress: pair?.pairAddress || null,
        dexId: dexId,
        // APR estimation
        aprEstimated,
        fees24hEstimated,
        feeRate,
        aprSource: "estimated",
      };
    });

    return results.slice(0, 20);
  } catch (err) {
    console.warn("Failed to fetch DexScreener trending:", err.message);
    return [];
  }
}

const DEX_FEE_RATES = {
  "uniswap-v2": 0.003,
  "sushiswap": 0.003,
  "uniswap-v3": 0.003,
  "pancakeswap": 0.0025,
  "pancakeswap-v2": 0.0025,
  "pancakeswap-v3": 0.0025,
  "curve": 0.0004,
  "curve-dex": 0.0004,
  "balancer": 0.003,
  "balancer-v2": 0.003,
  "raydium": 0.0025,
  "raydium-amm": 0.0025,
  "orca": 0.003,
  "orca-dex": 0.003,
  "aerodrome": 0.003,
  "aerodrome-v2": 0.003,
  "velodrome": 0.003,
  "velodrome-v2": 0.003,
};

const DEFAULT_FEE_RATE = 0.003;
const MAX_ESTIMATED_APR = 10000; // cap at 10,000% to filter anomalies
const MIN_RESERVE_USD = 1000; // minimum liquidity to produce a meaningful APR

function getDexFeeRate(dexId) {
  if (!dexId) return DEFAULT_FEE_RATE;
  const key = dexId.toLowerCase();
  return DEX_FEE_RATES[key] || DEFAULT_FEE_RATE;
}

function estimateApr(volumeH24, reserveUsd, feeRate) {
  if (!reserveUsd || reserveUsd < MIN_RESERVE_USD || !volumeH24 || volumeH24 <= 0) {
    return { aprEstimated: 0, fees24hEstimated: 0 };
  }
  const fees24h = volumeH24 * feeRate;
  const apr = (fees24h / reserveUsd) * 365 * 100;
  return {
    aprEstimated: Math.min(apr, MAX_ESTIMATED_APR),
    fees24hEstimated: fees24h,
  };
}

export async function fetchGeckoTerminalTrending() {
  try {
    const res = await fetch(GECKOTERMINAL_TRENDING_URL);
    if (!res.ok) throw new Error(`GeckoTerminal responded ${res.status}`);
    const json = await res.json();
    const pools = json.data || [];

    return pools.map((p, i) => {
      const attr = p.attributes || {};
      const network = p.relationships?.network?.data?.id || "";
      const dex = p.relationships?.dex?.data?.id || "";

      const volumeH24 = parseFloat(attr.volume_usd?.h24) || 0;
      const reserveUsd = parseFloat(attr.reserve_in_usd) || 0;
      const feeRate = getDexFeeRate(dex);
      const { aprEstimated, fees24hEstimated } = estimateApr(volumeH24, reserveUsd, feeRate);

      return {
        source: "geckoterminal",
        id: p.id,
        name: attr.name || "",
        chain: normalizeChain(network),
        dex: dex,
        priceUsd: parseFloat(attr.base_token_price_usd) || 0,
        fdv: parseFloat(attr.fdv_usd) || 0,
        marketCap: parseFloat(attr.market_cap_usd) || 0,
        reserveUsd,
        volumeH24,
        volumeH1: parseFloat(attr.volume_usd?.h1) || 0,
        priceChangeM5: parseFloat(attr.price_change_percentage?.m5) || 0,
        priceChangeH1: parseFloat(attr.price_change_percentage?.h1) || 0,
        priceChangeH24: parseFloat(attr.price_change_percentage?.h24) || 0,
        txnsH24Buys: attr.transactions?.h24?.buys || 0,
        txnsH24Sells: attr.transactions?.h24?.sells || 0,
        txnsH1Buys: attr.transactions?.h1?.buys || 0,
        txnsH1Sells: attr.transactions?.h1?.sells || 0,
        poolCreatedAt: attr.pool_created_at || null,
        baseToken: attr.name?.split(" / ")?.[0] || "",
        quoteToken: attr.name?.split(" / ")?.[1] || "",
        rank: i + 1,
        // APR estimation fields
        aprEstimated,
        fees24hEstimated,
        feeRate,
        aprSource: "estimated",
      };
    });
  } catch (err) {
    console.warn("Failed to fetch GeckoTerminal trending:", err.message);
    return [];
  }
}

export async function fetchDegenTrending() {
  const [dexScreener, geckoTerminal] = await Promise.all([
    fetchDexScreenerTrending(),
    fetchGeckoTerminalTrending(),
  ]);
  return { dexScreener, geckoTerminal };
}

// ── Market Overview API functions ──

export async function fetchCoinGeckoTrendingWithDetails() {
  try {
    const res = await fetch(COINGECKO_TRENDING_URL);
    if (!res.ok) throw new Error(`CoinGecko responded ${res.status}`);
    const json = await res.json();

    return (json.coins || []).map(entry => {
      const coin = entry.item;
      return {
        id: coin.id,
        name: coin.name,
        symbol: coin.symbol,
        thumb: coin.thumb,
        small: coin.small,
        large: coin.large,
        marketCapRank: coin.market_cap_rank,
        priceBtc: coin.price_btc,
        score: coin.score,
        priceUsd: coin.data?.price || null,
        priceChange24h: coin.data?.price_change_percentage_24h?.usd || null,
        marketCap: coin.data?.market_cap || null,
        totalVolume: coin.data?.total_volume || null,
        sparkline: coin.data?.sparkline || null,
      };
    });
  } catch (err) {
    console.warn("Failed to fetch CoinGecko trending with details:", err.message);
    return [];
  }
}

export function computeChainStats(pools) {
  const chainMap = {};

  for (const p of pools) {
    if (!chainMap[p.chain]) {
      chainMap[p.chain] = {
        chain: p.chain,
        totalTvl: 0,
        poolCount: 0,
        aprSum: 0,
        topPool: null,
        tvlChangeSum: 0,
        tvlChangeCount: 0,
        totalVolume24h: 0,
      };
    }
    const s = chainMap[p.chain];
    s.totalTvl += p.tvl || 0;
    s.poolCount += 1;
    s.aprSum += p.apr || 0;
    s.totalVolume24h += p.volume24h || 0;

    if (p.apyPct1D != null) {
      s.tvlChangeSum += p.apyPct1D;
      s.tvlChangeCount += 1;
    }

    if (!s.topPool || p.apr > s.topPool.apr) {
      s.topPool = { name: p.name, apr: p.apr };
    }
  }

  return Object.values(chainMap)
    .map(s => ({
      chain: s.chain,
      totalTvl: s.totalTvl,
      poolCount: s.poolCount,
      avgApr: s.poolCount > 0 ? s.aprSum / s.poolCount : 0,
      topPool: s.topPool,
      tvlChange: s.tvlChangeCount > 0 ? s.tvlChangeSum / s.tvlChangeCount : 0,
      totalVolume24h: s.totalVolume24h,
    }))
    .sort((a, b) => b.totalTvl - a.totalTvl);
}

export function computeSmartMoneySignals(pools) {
  const signals = [];
  const now = new Date();

  for (const p of pools) {
    // volume_spike: volume24h / tvl > 5 (unusual trading activity)
    if (p.volume24h && p.tvl && p.tvl > 0 && (p.volume24h / p.tvl) > 5) {
      signals.push({
        type: "volume_spike",
        pool: p,
        description: `${p.name} on ${p.chain} has unusual trading activity \u2014 volume is ${(p.volume24h / p.tvl).toFixed(1)}x its TVL`,
        magnitude: p.volume24h / p.tvl,
        timestamp: now,
      });
    }

    // apr_surge: apyPct1D > 20 (massive APR jump in 24h)
    if (p.apyPct1D != null && p.apyPct1D > 20) {
      signals.push({
        type: "apr_surge",
        pool: p,
        description: `${p.name} on ${p.chain} APR surged +${p.apyPct1D.toFixed(1)}% in the last 24h`,
        magnitude: p.apyPct1D,
        timestamp: now,
      });
    }

    // tvl_spike: tvl > 10M and apyPct1D exists (large established pool with movement)
    if (p.tvl > 10_000_000 && p.apyPct1D != null) {
      signals.push({
        type: "tvl_spike",
        pool: p,
        description: `${p.name} on ${p.chain} (${fmtCompact(p.tvl)} TVL) showing movement \u2014 APR shifted ${p.apyPct1D > 0 ? "+" : ""}${p.apyPct1D.toFixed(1)}% today`,
        magnitude: Math.abs(p.apyPct1D) * (p.tvl / 1_000_000),
        timestamp: now,
      });
    }

    // new_high: apr > 50 and tvl > 500K (high yield on decent liquidity)
    if (p.apr > 50 && p.tvl > 500_000) {
      signals.push({
        type: "new_high",
        pool: p,
        description: `${p.name} on ${p.chain} yielding ${p.apr.toFixed(1)}% APR with ${fmtCompact(p.tvl)} liquidity`,
        magnitude: p.apr * (p.tvl / 500_000),
        timestamp: now,
      });
    }
  }

  signals.sort((a, b) => b.magnitude - a.magnitude);
  return signals.slice(0, 20);
}

// Helper for signal and feed descriptions
function fmtCompact(n) {
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

export function computeMarketFeed(pools) {
  const items = [];
  const now = new Date();

  const byApr = [...pools].sort((a, b) => b.apr - a.apr);
  const byTvl = [...pools].sort((a, b) => b.tvl - a.tvl);
  const byVolume = [...pools].sort((a, b) => b.volume24h - a.volume24h);

  // new_opportunity: Top APR pools with decent TVL
  for (const p of byApr.slice(0, 4)) {
    if (p.tvl > 100_000) {
      items.push({
        type: "new_opportunity",
        title: `${p.apr.toFixed(1)}% APR on ${p.name}`,
        description: `${p.protocol} pool on ${p.chain} with ${fmtCompact(p.tvl)} TVL`,
        pool: p,
        icon: "\u{1F4B0}",
        timestamp: now,
        color: p.apr > 20 ? "#8A6A00" : "#0D7A4E",
      });
    }
  }

  // whale_activity: Highest volume pools
  for (const p of byVolume.slice(0, 3)) {
    if (p.volume24h > 1_000_000) {
      items.push({
        type: "whale_activity",
        title: `${fmtCompact(p.volume24h)} volume on ${p.name}`,
        description: `Heavy trading on ${p.protocol} ${p.chain} \u2014 ${fmtCompact(p.fees24h)} in fees collected`,
        pool: p,
        icon: "\u{1F433}",
        timestamp: now,
        color: "#5470D4",
      });
    }
  }

  // yield_alert: Pools with significant APR changes
  const withChanges = pools.filter(p => p.apyPct1D != null && Math.abs(p.apyPct1D) > 10);
  const sortedByChange = [...withChanges].sort((a, b) => Math.abs(b.apyPct1D) - Math.abs(a.apyPct1D));
  for (const p of sortedByChange.slice(0, 3)) {
    const direction = p.apyPct1D > 0 ? "up" : "down";
    items.push({
      type: "yield_alert",
      title: `${p.name} APR ${direction} ${Math.abs(p.apyPct1D).toFixed(1)}%`,
      description: `${p.protocol} on ${p.chain} \u2014 now at ${p.apr.toFixed(1)}% APR`,
      pool: p,
      icon: p.apyPct1D > 0 ? "\u{1F4C8}" : "\u{1F4C9}",
      timestamp: now,
      color: p.apyPct1D > 0 ? "#0D7A4E" : "#B8001A",
    });
  }

  // protocol_growth: Aggregate protocol stats
  const protocolTvl = {};
  for (const p of pools) {
    if (!protocolTvl[p.protocol]) protocolTvl[p.protocol] = { tvl: 0, count: 0, volume: 0 };
    protocolTvl[p.protocol].tvl += p.tvl;
    protocolTvl[p.protocol].count += 1;
    protocolTvl[p.protocol].volume += p.volume24h || 0;
  }
  const topProtocols = Object.entries(protocolTvl)
    .sort((a, b) => b[1].tvl - a[1].tvl)
    .slice(0, 3);
  for (const [name, data] of topProtocols) {
    items.push({
      type: "protocol_growth",
      title: `${name}: ${fmtCompact(data.tvl)} total TVL`,
      description: `${data.count} active pools, ${fmtCompact(data.volume)} 24h volume`,
      pool: null,
      icon: "\u{1F3E6}",
      timestamp: now,
      color: "#666666",
    });
  }

  // Safe stablecoin opportunities
  const stablePools = byTvl.filter(p => p.stablecoin);
  for (const p of stablePools.slice(0, 2)) {
    items.push({
      type: "new_opportunity",
      title: `Safe yield: ${p.apr.toFixed(1)}% on ${p.name}`,
      description: `Stablecoin pool on ${p.protocol} ${p.chain} \u2014 ${fmtCompact(p.tvl)} TVL, low risk`,
      pool: p,
      icon: "\u{1F6E1}\uFE0F",
      timestamp: now,
      color: "#0D7A4E",
    });
  }

  return items.slice(0, 15);
}
